import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight } from 'lucide-react'
import Seo from '../../../../components/Seo'

const stats = [
  { number: '$195B', label: 'annual cost of customer churn across U.S. banking' },
  { number: '25-95%', label: 'profit increase from just 5% improvement in retention' },
  { number: '5x', label: 'more expensive to acquire a new customer than retain one' },
]

const problems = [
  {
    tool: 'Surveys',
    issue: '5-10% response rates. The customers most likely to leave are the least likely to fill out a form.',
  },
  {
    tool: 'NPS Scores',
    issue: "A number without a narrative. You know the score, but not the story behind it.",
  },
  {
    tool: 'Exit Interviews',
    issue: "Happen after the decision is already made. By then, the insight is a post-mortem, not a prevention.",
  },
  {
    tool: 'Support Tickets',
    issue: "Capture complaints, not context. Most dissatisfied customers never open a ticket. They just leave.",
  },
]

const steps = [
  {
    number: 1,
    title: 'Engage',
    description: 'AI conducts natural, open-ended conversations with customers at key moments: onboarding, renewal, post-interaction, or when churn signals appear.',
  },
  {
    number: 2,
    title: 'Analyze',
    description: 'A two-stage AI pipeline extracts themes, sentiment, friction points, and unmet needs from every conversation. No survey fatigue. No leading questions.',
  },
  {
    number: 3,
    title: 'Act',
    description: 'An actionable intelligence report identifies the specific drivers of attrition, retention, and satisfaction, with recommendations prioritized by impact.',
  },
]

const comparisonRows = [
  { label: 'Response rate', traditional: '5-10%', prismscope: '85%+ completion' },
  { label: 'Depth of insight', traditional: 'Score only', prismscope: 'Score + full context' },
  { label: 'Timing', traditional: 'Post-decision', prismscope: 'Pre-decision' },
  { label: 'Cost per insight', traditional: '$10K+ per focus group', prismscope: 'Per conversation' },
  { label: 'Sentiment capture', traditional: 'Complaints only', prismscope: 'Full spectrum' },
  { label: 'Scalability', traditional: 'Manual, expensive', prismscope: 'AI-powered, on demand' },
]

const surfaces = [
  { title: 'Early warning signals', description: 'Identify why customers are considering leaving before they make the decision to go.' },
  { title: 'Fee and policy friction', description: 'Understand which fees, policies, or processes create the most customer frustration.' },
  { title: 'Digital experience gaps', description: 'Learn where your digital experience falls short of customer expectations.' },
  { title: 'Competitive intelligence', description: 'Discover what competitors are offering that draws your customers away.' },
  { title: 'Segment-level risk', description: 'Pinpoint which customer segments are most at risk and what drives attrition in each.' },
  { title: 'Retention drivers', description: 'Find out what would make at-risk customers stay, so you can act on it.' },
]

const roiRows = [
  { label: 'Annual churn at 18%', value: '1,800 customers lost' },
  { label: 'Acquisition cost per customer', value: '$300-500' },
  { label: 'Annual cost of churn', value: '$540K-$900K' },
  { label: '5% retention improvement', value: '90 customers retained' },
  { label: 'Value of improvement', value: '$135K-$450K saved' },
  { label: 'PrismScope annual program', value: '$15K-$20K', highlight: true },
  { label: 'Return on investment', value: '7x-30x', highlight: true },
]

const roadmap = [
  {
    phase: 'Available Now',
    color: 'bg-primary-700',
    items: [
      'AI-powered conversational interviews',
      'Two-stage analysis pipeline',
      'Automated theme and sentiment extraction',
      'Churn driver identification report',
      'Case study and testimonial output',
    ],
  },
  {
    phase: 'Q2 2026',
    color: 'bg-primary-600',
    items: [
      'CRM-triggered churn signal conversations',
      'Real-time retention monitoring dashboard',
      'Chat with your data',
      'Industry-specific question frameworks',
      'Quarterly pulse program',
    ],
  },
  {
    phase: 'Q3-Q4 2026',
    color: 'bg-primary-500',
    items: [
      'Predictive churn risk scoring',
      'Core platform integrations',
      'Automated retention playbooks',
      'Multi-language support',
      'SOC 2 Type II certification',
    ],
  },
]

export default function ChurnReduction() {
  return (
    <div>
      <Seo
        title="Churn Reduction"
        description="Cut customer churn with a continuous feedback loop — diagnose, act, and measure. Conversational research that turns churn signals into retention."
        path="/feedback-loops/customer/churn/reduction"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-intelligence-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops" className="hover:text-intelligence-900">Use Cases</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops/customer/churn" className="hover:text-intelligence-900">Churn Research</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Reduction</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-intelligence-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block px-4 py-1.5 bg-primary-500/15 border border-primary-500/25 rounded-full text-primary-400 text-sm font-semibold mb-6">
            Churn Intelligence Platform
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Stop guessing why customers leave.{' '}
            <span className="text-primary-400">Start knowing.</span>
          </h1>
          <p className="text-lg text-intelligence-400 max-w-2xl mb-8">
            PrismScope uses AI-powered conversations to identify the specific drivers of customer attrition, before your customers walk out the door.
          </p>
          <a
            href="https://rays.prismscope.ai/admin"
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
          >
            Start a Pilot <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Stat Banner */}
      <section className="bg-intelligence-50 border-b border-intelligence-200 py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.number}>
                <div className="text-3xl md:text-4xl font-bold text-prism-red mb-1">{stat.number}</div>
                <p className="text-sm text-intelligence-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="bg-intelligence-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            The real problem isn&apos;t churn.<br />It&apos;s not knowing why.
          </h2>
          <p className="text-lg text-intelligence-400 text-center max-w-2xl mx-auto mb-10">
            The tools most companies rely on for customer insight have fundamental limitations.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {problems.map((problem) => (
              <div
                key={problem.tool}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/[0.07] hover:border-primary-500/30 transition-all"
              >
                <div className="text-xs font-semibold text-primary-400 uppercase tracking-wider mb-2">
                  {problem.tool}
                </div>
                <p className="text-intelligence-300 text-sm">{problem.issue}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              How PrismScope works
            </h2>
            <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
              AI-powered conversational intelligence that surfaces why customers stay, leave, or hesitate.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-14 h-14 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-intelligence-900 mb-2">{step.title}</h3>
                <p className="text-intelligence-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              PrismScope vs. traditional methods
            </h2>
            <p className="text-lg text-intelligence-600">
              The difference between measuring sentiment and understanding it.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-intelligence-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-intelligence-200">
                    <th className="text-left py-4 px-5 text-intelligence-600 font-medium text-sm" />
                    <th className="py-4 px-5 text-intelligence-500 font-medium text-sm uppercase tracking-wider bg-intelligence-50">
                      Traditional
                    </th>
                    <th className="py-4 px-5 text-white font-medium text-sm uppercase tracking-wider bg-primary-700">
                      PrismScope
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.label} className="border-t border-intelligence-100">
                      <td className="py-3 px-5 font-semibold text-intelligence-900 text-sm">{row.label}</td>
                      <td className="py-3 px-5 text-intelligence-500 text-sm text-center">{row.traditional}</td>
                      <td className="py-3 px-5 text-primary-700 font-semibold text-sm text-center">{row.prismscope}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* What PrismScope Surfaces */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              What PrismScope surfaces
            </h2>
            <p className="text-lg text-intelligence-600">
              The intelligence you need to reduce churn, in your customers&apos; own words.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {surfaces.map((item) => (
              <div
                key={item.title}
                className="border-l-[3px] border-primary-500 bg-intelligence-50 rounded-r-lg p-5 hover:bg-primary-50 transition-colors"
              >
                <h4 className="font-semibold text-intelligence-900 mb-1">{item.title}</h4>
                <p className="text-sm text-intelligence-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="bg-primary-50 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              The ROI of knowing why
            </h2>
            <p className="text-lg text-intelligence-600">
              A small improvement in retention creates outsized impact on the bottom line.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-intelligence-200 p-6 md:p-8 shadow-sm">
            <h3 className="text-lg font-semibold text-primary-700 mb-5">
              Example: 10,000 customer base
            </h3>
            <div className="divide-y divide-intelligence-100">
              {roiRows.map((row) => (
                <div
                  key={row.label}
                  className={`flex justify-between py-3 ${row.highlight ? 'text-primary-700 font-semibold' : ''}`}
                >
                  <span className={row.highlight ? '' : 'text-intelligence-700'}>{row.label}</span>
                  <span className={row.highlight ? 'text-lg' : 'font-semibold text-intelligence-900'}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Product roadmap
            </h2>
            <p className="text-lg text-intelligence-600">
              From pilot to ongoing churn intelligence program.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {roadmap.map((phase) => (
              <div key={phase.phase} className="bg-white rounded-xl border border-intelligence-200 overflow-hidden shadow-sm">
                <div className={`${phase.color} text-white font-semibold text-center py-3`}>
                  {phase.phase}
                </div>
                <ul className="p-5 space-y-2">
                  {phase.items.map((item) => (
                    <li key={item} className="flex items-start text-sm text-intelligence-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 mr-3 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Let&apos;s find out why your customers are leaving.
          </h2>
          <p className="text-lg text-intelligence-400 mb-8">
            Start with a focused pilot: 50 AI-powered conversations, a 4-6 week engagement, and an actionable intelligence report identifying your top churn drivers.
          </p>
          <a
            href="https://rays.prismscope.ai/admin"
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-intelligence-900 bg-white hover:bg-intelligence-100 rounded-lg shadow-md transition-colors"
          >
            Schedule a Conversation <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  )
}
